import React, { useMemo } from 'react'
import { pdf } from '@react-pdf/renderer'
import { useQuery } from '@tanstack/react-query'
import utilsMergePdfs from 'report/utils/mergePdfs'

const emptyBlobsArray: Readonly<Blob[]> = []

const getReactPDFBuffers = (
  components: React.JSX.Element[]
): Promise<ArrayBuffer>[] =>
  components.map(async (component) =>
    (await pdf(component).toBlob()).arrayBuffer()
  )

export type UseReportPdfsProps = {
  components: React.JSX.Element[]
  sourceObjects: object[]
  shouldMerge: boolean
  getPdfBuffers?: typeof getReactPDFBuffers
  mergePdfs?: typeof utilsMergePdfs
  enabled?: boolean
}

export default function useReportPdfs({
  components,
  sourceObjects,
  shouldMerge,
  getPdfBuffers = getReactPDFBuffers,
  mergePdfs = utilsMergePdfs,
  enabled
}: UseReportPdfsProps) {
  const {
    data: blobs = emptyBlobsArray,
    isFetching: isRendering,
    isFetched: isRenderFinished,
    error
  } = useQuery({
    queryKey: [
      'useReportPdfs',
      shouldMerge ? 'merge' : 'split',
      ...sourceObjects
    ],
    queryFn: async () => {
      const pdfBuffers = await Promise.all(getPdfBuffers(components))

      if (pdfBuffers.length === 0) {
        return []
      }

      const finishedBuffers = shouldMerge
        ? [await mergePdfs(pdfBuffers)]
        : pdfBuffers

      return finishedBuffers.map(
        (finishedBuffer) =>
          new Blob([finishedBuffer], {
            type: 'application/pdf'
          })
      )
    },
    retry: false,
    enabled
  })

  const urls = useMemo(
    () => blobs.map((blob) => URL.createObjectURL(blob)),
    [blobs]
  )

  return {
    blobs,
    urls,
    isRendering,
    isRenderFinished,
    error
  }
}
